import type { ReactNode } from 'react';
import type { ProjectPageLoader } from '@modules/project/type';

interface DetailsItems {
    itemName: string;
    value: string;
    link?: string;
    isExternal?: boolean;
}

export type DetailsItemsData = Partial<
    Pick<
        ProjectPageLoader['project'],
        | 'Location'
        | 'YearOfCompletion'
        | 'ProjectPriceRange'
        | 'BuildingStyle'
        | 'Client'
        | 'LibraryDownloads'
        | 'Awards'
    >
>;
export const getDetailsItems = (
    data: DetailsItemsData,
    includeAddress: boolean = false,
    includeDownloads: boolean = true
): DetailsItems[] => {
    const detailsItems = [];

    if (includeAddress && data.Location) {
        detailsItems.push({
            itemName: 'Region',
            value: [data.Location.Suburb, data.Location.Region]
                .filter((t) => (t ?? '').trim().length > 0)
                .join(', '),
        });
    }

    if (data.YearOfCompletion && data.YearOfCompletion !== 0) {
        detailsItems.push({
            itemName: 'Year of completion',
            value: String(data.YearOfCompletion),
        });
    }

    if (data.ProjectPriceRange) {
        detailsItems.push({
            itemName: 'Price range to build',
            value: data.ProjectPriceRange,
        });
    }

    if (data.BuildingStyle) {
        detailsItems.push({
            itemName: 'Building style',
            value: data.BuildingStyle.replaceAll('_', ' '),
        });
    }

    if (data.Client) {
        detailsItems.push({
            itemName: 'Client',
            value: data.Client,
        });
    }

    if (data.Awards && data.Awards.length !== 0) {
        data.Awards.forEach((award, index) => {
            detailsItems.push({
                itemName: index === 0 ? 'Awards' : '',
                value: `${award.title} ${award.year ? `- ${award.year}` : ''}`,
            });
        });
    }

    if (
        includeDownloads &&
        data.LibraryDownloads &&
        data.LibraryDownloads.length !== 0
    ) {
        const fileCategories = new Map<string, boolean>();
        data.LibraryDownloads.forEach((item) => {
            const categoryName = item.FileCategory?.Title ?? 'Plans';
            const label = fileCategories.has(categoryName) ? '' : categoryName;
            fileCategories.set(categoryName, true);

            detailsItems.push({
                itemName: label,
                value: item.Title,
                link: item.InternalLink || item.ExternalLink,
                isExternal: !!item.ExternalLink,
            });
        });
    }

    return detailsItems;
};

export const displayItems = (
    items: DetailsItems[],
    showItem: (
        label: string,
        value: string,
        link?: string,
        isExternal?: boolean
    ) => ReactNode
) => {
    return items.map((item) => {
        return showItem(item.itemName, item.value, item.link, item.isExternal);
    });
};
